/**
 * CandidateDetail — Fiche candidat modale pleine page
 * CV, commentaires multi-utilisateurs, entretiens, analyse IA
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X, FileText, Download, Star, MessageSquare, Send, Trash2,
  Phone, Mail, Calendar, Clock, Building, User, Loader, Brain,
  CheckCircle, XCircle, Upload, Edit, Save, ChevronDown, ExternalLink, StickyNote, UserPlus
} from 'lucide-react';
import {
  getRecruitmentApplication, createRecruitmentComment,
  deleteRecruitmentComment, uploadRecruitmentCV,
  analyzeRecruitmentCV, updateRecruitmentApplication,
  updateRecruitmentInterview, completeRecruitmentInterview,
  convertApplicationToEmployee,
} from '../api';
import { useToast } from '../contexts/NotificationContext';
import Modal from './ui/Modal';

const STATUS_CONFIG = {
  nouveau:            { label: 'Nouveau',           color: 'bg-gray-100 text-gray-700' },
  présélectionné:     { label: 'Présélectionné',    color: 'bg-blue-100 text-blue-700' },
  entretien_tel:      { label: 'Entretien tél.',    color: 'bg-indigo-100 text-indigo-700' },
  entretien_physique: { label: 'Entretien physique', color: 'bg-purple-100 text-purple-700' },
  visite_structure:   { label: 'Visite structure',  color: 'bg-teal-100 text-teal-700' },
  offre_envoyée:      { label: 'Offre envoyée',     color: 'bg-amber-100 text-amber-700' },
  embauché:           { label: 'Embauché',          color: 'bg-green-100 text-green-700' },
  refusé:             { label: 'Refusé',            color: 'bg-red-100 text-red-700' },
  archivé:            { label: 'Archivé',           color: 'bg-gray-100 text-gray-400' },
};

const ROUND_LABELS = { 1: 'Telephonique', 2: 'Presentiel', 3: 'Visite structure' };
const ROUND_COLORS = { 1: 'bg-blue-100 text-blue-700', 2: 'bg-purple-100 text-purple-700', 3: 'bg-teal-100 text-teal-700' };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const RatingStars = ({ rating, onChange, size = 16 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange?.(n)}
        className={`${n <= (rating || 0) ? 'text-amber-400' : 'text-gray-300'} ${onChange ? 'cursor-pointer hover:text-amber-500' : ''}`}>
        <Star size={size} fill={n <= (rating || 0) ? 'currentColor' : 'none'} />
      </button>
    ))}
  </div>
);

const CandidateDetail = ({ application, grids, templates, structures, offers, positions = [], onClose, onStatusChange, onDelete, onRefresh }) => {
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [activeSection, setActiveSection] = useState('info');
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedGridId, setSelectedGridId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null); // interview object being edited
  const [itwForm, setItwForm] = useState({ answers: {}, notes: '', rating: 0, outcome: 'pending' });
  const [savingItw, setSavingItw] = useState(false);
  const [showCvPreview, setShowCvPreview] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      const { data } = await getRecruitmentApplication(application.id);
      setDetail(data);
      setComments(data.comments || []);
      setInterviews(data.interviews || []);
      setNotesText(data.notes || '');
    } catch (e) { console.error('loadDetail:', e); }
  }, [application.id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const app = detail || application;
  const tags = app.tags ? (typeof app.tags === 'string' ? JSON.parse(app.tags) : app.tags) : [];
  const latestEval = app.evaluations?.[0];

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await createRecruitmentComment(app.id, { content: newComment.trim() });
      setNewComment('');
      loadDetail();
    } catch (e) { toast.error('Erreur ajout commentaire'); }
  };

  const handleDeleteComment = async (commentId) => {
    try { await deleteRecruitmentComment(commentId); loadDetail(); }
    catch (e) { toast.error('Erreur suppression'); }
  };

  const handleUploadCV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('cv', file);
      await uploadRecruitmentCV(app.id, formData);
      toast.success('CV uploade');
      loadDetail();
    } catch (err) { toast.error('Erreur upload'); }
    setUploading(false);
  };

  const handleSaveNotes = async () => {
    if (notesText === (app.notes || '')) return;
    setSavingNotes(true);
    try {
      await updateRecruitmentApplication(app.id, { notes: notesText });
      toast.success('Notes sauvegardees');
      loadDetail();
    } catch (err) { toast.error('Erreur sauvegarde notes'); }
    setSavingNotes(false);
  };

  const handleAnalyzeCV = async () => {
    if (!selectedGridId) { toast.error('Selectionnez une grille d\'evaluation'); return; }
    setAnalyzing(true);
    try {
      await analyzeRecruitmentCV(app.id, { gridId: selectedGridId });
      toast.success('Analyse terminee');
      loadDetail();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur analyse'); }
    setAnalyzing(false);
  };

  const handleRating = async (rating) => {
    try {
      await updateRecruitmentApplication(app.id, { rating });
      loadDetail();
      onRefresh();
    } catch (e) { toast.error('Erreur'); }
  };

  // Phase 3 process embauche : conversion candidat → employé
  const [convertingToEmployee, setConvertingToEmployee] = useState(false);
  const handleConvertToEmployee = async () => {
    if (!window.confirm(
      `Créer un employé pour ${app.candidateName} ?\n\n` +
      `Les infos candidat (nom, email, téléphone, poste, structure) seront pré-remplies. ` +
      `Tu pourras compléter la fiche ensuite.`
    )) return;
    setConvertingToEmployee(true);
    try {
      const { data: employee } = await convertApplicationToEmployee(app.id);
      toast.success(`Employé créé : ${employee.firstName} ${employee.lastName}`);
      onRefresh();
      // Recharge la candidature pour avoir le lien employee.id à jour
      loadDetail();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Erreur création employé';
      const existingId = e?.response?.data?.employeeId;
      if (existingId) {
        toast.error(msg + ' (déjà existant)');
      } else {
        toast.error(msg);
      }
    }
    setConvertingToEmployee(false);
  };

  const startEditing = () => {
    setEditing(true);
    setEditForm({
      candidateName: app.candidateName || '',
      candidateEmail: app.candidateEmail || '',
      candidatePhone: app.candidatePhone || '',
      position: app.position || '',
      source: app.source || '',
      diplomas: app.diplomas || '',
      notes: app.notes || '',
      offerId: app.offerId || '',
      tags: tags.join(', '),
    });
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const tagsArray = editForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      await updateRecruitmentApplication(app.id, {
        ...editForm,
        offerId: editForm.offerId || null,
        tags: JSON.stringify(tagsArray),
      });
      toast.success('Candidature mise a jour');
      setEditing(false);
      loadDetail();
      onRefresh();
    } catch (e) { toast.error('Erreur mise a jour'); }
    setSavingEdit(false);
  };

  // ─── Interview Handlers ─────────────────────────────────────────────────

  const startEditInterview = (itw) => {
    const answers = itw.answers || {};
    // Si le template a des questions, initialiser les reponses vides
    if (itw.template?.questions && typeof answers !== 'object') {
      try { Object.assign(answers, {}); } catch {}
    }
    setEditingInterview(itw);
    setItwForm({
      answers: typeof answers === 'string' ? JSON.parse(answers) : (answers || {}),
      notes: itw.notes || '',
      rating: itw.rating || 0,
      outcome: itw.outcome || 'pending',
    });
  };

  const handleSaveInterview = async (complete = false) => {
    if (!editingInterview) return;
    setSavingItw(true);
    try {
      const payload = {
        answers: itwForm.answers,
        notes: itwForm.notes,
        rating: itwForm.rating || null,
        outcome: itwForm.outcome,
      };
      if (complete) {
        await completeRecruitmentInterview(editingInterview.id, payload);
        toast.success('Entretien complete');
      } else {
        await updateRecruitmentInterview(editingInterview.id, payload);
        toast.success('Entretien mis a jour');
      }
      setEditingInterview(null);
      loadDetail();
      onRefresh();
    } catch (e) { toast.error('Erreur sauvegarde entretien'); }
    setSavingItw(false);
  };

  const setAnswer = (questionId, value) => {
    setItwForm(f => ({ ...f, answers: { ...f.answers, [questionId]: value } }));
  };

  // ─── Sections ────────────────────────────────────────────────────────────

  const sections = [
    { id: 'info', label: 'Informations', icon: User },
    { id: 'cv', label: 'CV & Analyse', icon: FileText },
    { id: 'comments', label: `Commentaires (${comments.length})`, icon: MessageSquare },
    { id: 'interviews', label: `Entretiens (${interviews.length})`, icon: Calendar },
  ];

  return (
    <Modal isOpen={true} onClose={onClose} title="" size="xl" preventClose={!!editingInterview || editing}>
      {/* Header candidat */}
      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-200">
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-800">{app.candidateName}</h2>
          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
            <span className="font-medium">{app.position}</span>
            {app.structure && <span className="text-xs flex items-center gap-1"><Building size={12} />{app.structure.name}</span>}
            {app.offer && <span className="text-xs text-violet-600 bg-violet-50 px-2 py-0.5 rounded">{app.offer.title}</span>}
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <select
              value={app.status}
              onChange={e => onStatusChange(app.id, e.target.value)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border-0 cursor-pointer ${STATUS_CONFIG[app.status]?.color || 'bg-gray-100'}`}
            >
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <RatingStars rating={app.rating} onChange={handleRating} />
            {/* Bouton "Créer employé" : visible uniquement si statut embauché et pas déjà créé */}
            {app.status === 'embauché' && !app.employee && (
              <button
                onClick={handleConvertToEmployee}
                disabled={convertingToEmployee}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-full hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
                title="Créer une fiche employé pré-remplie depuis cette candidature"
              >
                {convertingToEmployee ? <Loader size={12} className="animate-spin" /> : <UserPlus size={12} />}
                {convertingToEmployee ? 'Création...' : 'Créer un employé'}
              </button>
            )}
            {app.employee && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full">
                <CheckCircle size={12} /> Employé créé
              </span>
            )}
            <span className="text-xs text-gray-400 ml-auto">Cree le {fmtDate(app.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeSection === s.id ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={14} />{s.label}
            </button>
          );
        })}
      </div>

      {/* ═══ INFO SECTION ═══ */}
      {activeSection === 'info' && (
        <div className="space-y-4">
          {editing ? (
            /* Mode edition */
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom du candidat *</label>
                  <input type="text" value={editForm.candidateName} onChange={e => setEditForm(f => ({ ...f, candidateName: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Poste *</label>
                  <select value={editForm.position} onChange={e => setEditForm(f => ({ ...f, position: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">Selectionner...</option>
                    {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                    {editForm.position && !positions.find(p => p.name === editForm.position) && (
                      <option value={editForm.position}>{editForm.position} (actuel)</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input type="email" value={editForm.candidateEmail} onChange={e => setEditForm(f => ({ ...f, candidateEmail: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Telephone</label>
                  <input type="tel" value={editForm.candidatePhone} onChange={e => setEditForm(f => ({ ...f, candidatePhone: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
                  <select value={editForm.source} onChange={e => setEditForm(f => ({ ...f, source: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">—</option>
                    {['France Travail', 'Indeed', 'LinkedIn', 'Mail', 'Spontane', 'Autre'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Offre liee</label>
                  <select value={editForm.offerId} onChange={e => setEditForm(f => ({ ...f, offerId: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">Aucune</option>
                    {offers.filter(o => o.status === 'published').map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Diplomes</label>
                <input type="text" value={editForm.diplomas} onChange={e => setEditForm(f => ({ ...f, diplomas: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="CAP AEPE, BEP CSS..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tags (separes par virgule)</label>
                <input type="text" value={editForm.tags} onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="APE, CAP AEPE, bilingue..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea rows={3} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={handleSaveEdit} disabled={savingEdit || !editForm.candidateName || !editForm.position}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
                  {savingEdit ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
                </button>
                <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              </div>
            </div>
          ) : (
            /* Mode lecture */
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  {app.candidateEmail && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={14} className="text-gray-400 flex-shrink-0" />
                      <a href={`mailto:${app.candidateEmail}`} className="text-blue-600 hover:underline">{app.candidateEmail}</a>
                    </div>
                  )}
                  {app.candidatePhone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone size={14} className="text-gray-400 flex-shrink-0" />
                      <a href={`tel:${app.candidatePhone}`} className="text-blue-600 hover:underline">{app.candidatePhone}</a>
                    </div>
                  )}
                  {app.source && (
                    <div className="text-sm"><span className="text-gray-500">Source :</span> <span className="font-medium">{app.source}</span></div>
                  )}
                  {app.diplomas && (
                    <div className="text-sm"><span className="text-gray-500">Diplomes :</span> <span className="font-medium">{app.diplomas}</span></div>
                  )}
                </div>
                <div className="space-y-3">
                  {tags.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-500">Tags</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {tags.map((t, i) => <span key={i} className="px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full text-xs">{t}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes rapides */}
              <div className="mt-4 pt-4 border-t">
                <label className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-2">
                  <StickyNote size={12} /> Notes rapides
                </label>
                <textarea
                  value={notesText}
                  onChange={e => setNotesText(e.target.value)}
                  onBlur={handleSaveNotes}
                  placeholder="Premieres impressions, remarques sur le CV..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-300 focus:border-violet-400 resize-none"
                />
                {savingNotes && <p className="text-xs text-gray-400 mt-1">Sauvegarde...</p>}
              </div>

              <div className="flex items-center gap-2 pt-3 border-t">
                <button onClick={startEditing} className="flex items-center gap-1 px-3 py-1.5 text-xs text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100">
                  <Edit size={12} /> Modifier les informations
                </button>
                <button onClick={() => onDelete(app.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 bg-red-50 rounded-lg hover:bg-red-100 ml-auto">
                  <Trash2 size={12} /> Supprimer
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ CV & ANALYSE SECTION ═══ */}
      {activeSection === 'cv' && (
        <div className="space-y-5">
          {/* CV */}
          {app.cvUrl ? (
            <div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{app.cvName || 'CV.pdf'}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <button onClick={() => setShowCvPreview(!showCvPreview)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        <FileText size={12} /> {showCvPreview ? 'Masquer' : 'Visualiser'}
                      </button>
                      <a href={app.cvUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:underline flex items-center gap-1">
                        <ExternalLink size={12} /> Ouvrir dans un nouvel onglet
                      </a>
                    </div>
                  </div>
                </div>
                <label className="cursor-pointer px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">
                  {uploading ? <Loader size={12} className="animate-spin" /> : 'Remplacer'}
                  <input type="file" accept=".pdf" onChange={handleUploadCV} className="hidden" />
                </label>
              </div>
              {showCvPreview && (
                <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden" style={{ height: '500px' }}>
                  <iframe src={app.cvUrl} className="w-full h-full" title="Preview CV" />
                </div>
              )}
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload size={28} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500 mb-3">Aucun CV attache</p>
              <label className="cursor-pointer px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 inline-flex items-center gap-1">
                {uploading ? <><Loader size={14} className="animate-spin" /> Upload...</> : <><Upload size={14} /> Uploader un CV</>}
                <input type="file" accept=".pdf" onChange={handleUploadCV} className="hidden" />
              </label>
            </div>
          )}

          {/* Analyse IA */}
          {app.cvUrl && (
            <div className="bg-violet-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-violet-800 flex items-center gap-2 mb-3">
                <Brain size={16} /> Analyse IA du CV
              </h4>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-2">
                <select value={selectedGridId} onChange={e => setSelectedGridId(e.target.value)} className="flex-1 border rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Choisir une grille d'evaluation</option>
                  {grids.map(g => <option key={g.id} value={g.id}>{g.name} {g.position ? `(${g.position})` : ''}</option>)}
                </select>
                <button onClick={handleAnalyzeCV} disabled={analyzing || !selectedGridId}
                  className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap">
                  {analyzing ? <><Loader size={14} className="animate-spin" /> Analyse en cours...</> : <><Brain size={14} /> Analyser</>}
                </button>
              </div>
              <p className="text-xs text-violet-500">Analyse a la demande pour maitriser les couts LLM.</p>
            </div>
          )}

          {/* Resultat analyse */}
          {latestEval && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Brain size={14} className="text-violet-500" />
                  Derniere analyse — {latestEval.grid?.name}
                </h4>
                <span className="text-xs text-gray-400">{fmtDate(latestEval.createdAt)}</span>
              </div>
              <div className="p-4">
                {latestEval.extractedScores && (
                  <div className="flex items-center gap-4 mb-4 p-3 bg-violet-50 rounded-lg">
                    <div className="text-center">
                      <span className="text-3xl font-bold text-violet-600">{latestEval.extractedScores.total}</span>
                      <span className="text-sm text-gray-500 ml-1">pts</span>
                    </div>
                    {latestEval.extractedScores.avisFinal && (
                      <div className="flex-1">
                        <span className="text-sm font-semibold text-gray-700">Avis final</span>
                        <p className="text-sm text-gray-600">{latestEval.extractedScores.avisFinal}</p>
                      </div>
                    )}
                  </div>
                )}
                {/* Scores detail */}
                {latestEval.extractedScores?.criteria && (
                  <div className="space-y-2 mb-4">
                    {latestEval.extractedScores.criteria.map((c, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-40 truncate">{c.name}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className="bg-violet-400 rounded-full h-2 transition-all" style={{ width: `${(c.score / c.maxPoints) * 100}%` }} />
                        </div>
                        <span className="text-xs font-medium text-gray-700 w-12 text-right">{c.score}/{c.maxPoints}</span>
                      </div>
                    ))}
                  </div>
                )}
                {latestEval.markdownOutput && (
                  <details className="group" open>
                    <summary className="cursor-pointer text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
                      <ChevronDown size={12} className="transition-transform group-open:rotate-180" />
                      Detail de l'analyse
                    </summary>
                    <div className="mt-3 overflow-x-auto">
                      {/* Parse markdown table if present */}
                      {(() => {
                        const lines = latestEval.markdownOutput.split('\n');
                        const tableLines = lines.filter(l => l.includes('|') && l.trim().startsWith('|'));
                        const textLines = lines.filter(l => !l.includes('|') || !l.trim().startsWith('|'));

                        return (
                          <>
                            {tableLines.length >= 3 && (
                              <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden mb-3">
                                <thead>
                                  <tr className="bg-violet-50">
                                    {tableLines[0].split('|').filter(Boolean).map((h, i) => (
                                      <th key={i} className="px-3 py-2 text-left text-violet-700 font-semibold border-b border-violet-100">
                                        {h.trim()}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {tableLines.slice(2).map((row, ri) => (
                                    <tr key={ri} className="hover:bg-gray-50">
                                      {row.split('|').filter(Boolean).map((cell, ci) => (
                                        <td key={ci} className="px-3 py-2 text-gray-700">{cell.trim()}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {textLines.filter(l => l.trim()).length > 0 && (
                              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-1">
                                {textLines.filter(l => l.trim()).map((line, i) => {
                                  const trimmed = line.trim();
                                  if (trimmed.startsWith('##')) return <h4 key={i} className="font-semibold text-gray-800 mt-2">{trimmed.replace(/^#+\s*/, '')}</h4>;
                                  if (trimmed.startsWith('#')) return <h3 key={i} className="font-bold text-gray-800 mt-2">{trimmed.replace(/^#+\s*/, '')}</h3>;
                                  if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) return <p key={i} className="pl-3">• {trimmed.slice(2)}</p>;
                                  if (trimmed.startsWith('**')) return <p key={i} className="font-semibold text-gray-700">{trimmed.replace(/\*\*/g, '')}</p>;
                                  return <p key={i}>{trimmed}</p>;
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ COMMENTS SECTION ═══ */}
      {activeSection === 'comments' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text" value={newComment} onChange={e => setNewComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddComment(); }}
              placeholder="Ajouter un commentaire..."
              className="flex-1 border rounded-lg px-3 py-2.5 text-sm"
            />
            <button onClick={handleAddComment} disabled={!newComment.trim()} className="px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1">
              <Send size={14} /> Envoyer
            </button>
          </div>
          <div className="space-y-3">
            {comments.map(c => (
              <div key={c.id} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-bold">
                      {c.authorName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{c.authorName}</span>
                    <span className="text-xs text-gray-400">{fmtDateTime(c.createdAt)}</span>
                  </div>
                  <button onClick={() => handleDeleteComment(c.id)} className="text-gray-300 hover:text-red-500 transition">
                    <Trash2 size={12} />
                  </button>
                </div>
                <p className="text-sm text-gray-600 pl-9">{c.content}</p>
              </div>
            ))}
            {comments.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucun commentaire</p>}
          </div>
        </div>
      )}

      {/* ═══ INTERVIEWS SECTION ═══ */}
      {activeSection === 'interviews' && (
        <div className="space-y-3">
          {editingInterview ? (
            /* ─── Formulaire entretien ─── */
            <div className="bg-white border border-violet-200 rounded-lg p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROUND_COLORS[editingInterview.round] || 'bg-gray-100'}`}>
                    Round {editingInterview.round} — {ROUND_LABELS[editingInterview.round]}
                  </span>
                  {editingInterview.completedAt ? 'Modifier l\'entretien' : 'Conduire l\'entretien'}
                </h4>
                <button onClick={() => setEditingInterview(null)} className="text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              </div>

              {/* Questions du template */}
              {/* Utilise questionsSnapshot (figé) en priorité, sinon template.questions */}
              {(editingInterview.questionsSnapshot || editingInterview.template?.questions) && Array.isArray(editingInterview.questionsSnapshot || editingInterview.template?.questions) && (
                <div className="space-y-4">
                  <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Questions — {editingInterview.template.name}</h5>
                  {(editingInterview.questionsSnapshot || editingInterview.template.questions).map((q, idx) => (
                    q.type === 'section' ? (
                      <div key={q.id || idx} className="border-b-2 border-indigo-200 pb-1 pt-3">
                        <h4 className="text-sm font-bold text-indigo-700 uppercase tracking-wider">{q.text || q.question || q.label}</h4>
                      </div>
                    ) : (
                    <div key={q.id || idx} className="bg-gray-50 rounded-lg p-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {q.text || q.question || q.label}
                      </label>
                      {q.type === 'rating' ? (
                        <div className="flex items-center gap-2">
                          <RatingStars rating={itwForm.answers[q.id || idx] || 0} onChange={(v) => setAnswer(q.id || String(idx), v)} size={20} />
                          <span className="text-xs text-gray-400">{itwForm.answers[q.id || idx] || 0}/5</span>
                        </div>
                      ) : (q.type === 'radio' || q.type === 'checkbox') && q.options?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {q.options.map(opt => {
                            const key = q.id || String(idx);
                            if (q.type === 'checkbox') {
                              const selected = Array.isArray(itwForm.answers[key]) ? itwForm.answers[key] : [];
                              const isChecked = selected.includes(opt);
                              return (
                                <label key={opt} className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer border transition ${
                                  isChecked ? 'bg-violet-100 border-violet-300 text-violet-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                }`}>
                                  <input type="checkbox" checked={isChecked} onChange={() => {
                                    const newVal = isChecked ? selected.filter(s => s !== opt) : [...selected, opt];
                                    setAnswer(key, newVal);
                                  }} className="hidden" />
                                  {opt}
                                </label>
                              );
                            }
                            return (
                              <label key={opt} className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer border transition ${
                                itwForm.answers[key] === opt ? 'bg-violet-100 border-violet-300 text-violet-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}>
                                <input type="radio" name={`q-${key}`} value={opt} checked={itwForm.answers[key] === opt}
                                  onChange={() => setAnswer(key, opt)} className="hidden" />
                                {opt}
                              </label>
                            );
                          })}
                        </div>
                      ) : q.type === 'textarea' ? (
                        <textarea rows={3} value={itwForm.answers[q.id || idx] || ''}
                          onChange={e => setAnswer(q.id || String(idx), e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Votre reponse..." />
                      ) : (
                        <input type="text" value={itwForm.answers[q.id || idx] || ''}
                          onChange={e => setAnswer(q.id || String(idx), e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Votre reponse..." />
                      )}
                    </div>
                    )
                  ))}
                </div>
              )}

              {/* Notes libres */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea rows={3} value={itwForm.notes} onChange={e => setItwForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Impressions, points a creuser..." />
              </div>

              {/* Note globale + Issue */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Note globale</label>
                  <RatingStars rating={itwForm.rating} onChange={(v) => setItwForm(f => ({ ...f, rating: v }))} size={20} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Issue</label>
                  <select value={itwForm.outcome} onChange={e => setItwForm(f => ({ ...f, outcome: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="pending">En attente</option>
                    <option value="passed">Retenu</option>
                    <option value="failed">Non retenu</option>
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <button onClick={() => handleSaveInterview(false)} disabled={savingItw}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
                  {savingItw ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
                </button>
                {!editingInterview.completedAt && (
                  <button onClick={() => handleSaveInterview(true)} disabled={savingItw}
                    className="flex items-center gap-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                    <CheckCircle size={14} /> Completer l'entretien
                  </button>
                )}
                <button onClick={() => setEditingInterview(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            /* ─── Liste entretiens ─── */
            <>
              {interviews.map(itw => (
                <div key={itw.id} className={`border rounded-lg p-4 ${itw.completedAt ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROUND_COLORS[itw.round] || 'bg-gray-100'}`}>
                      Round {itw.round} — {ROUND_LABELS[itw.round]}
                    </span>
                    <div className="flex items-center gap-2">
                      {itw.completedAt ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${itw.outcome === 'passed' ? 'bg-green-100 text-green-700' : itw.outcome === 'failed' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                          {itw.outcome === 'passed' ? 'Retenu' : itw.outcome === 'failed' ? 'Non retenu' : 'En attente'}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-500 font-medium">Planifie</span>
                      )}
                      {itw.rating > 0 && <RatingStars rating={itw.rating} size={12} />}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    {itw.scheduledAt && <div className="flex items-center gap-1"><Clock size={12} />{fmtDateTime(itw.scheduledAt)}</div>}
                    {itw.interviewers && <div className="flex items-center gap-1"><User size={12} />{itw.interviewers}</div>}
                    {itw.structure && <div className="flex items-center gap-1"><Building size={12} />{itw.structure.name}</div>}
                  </div>
                  {itw.notes && <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap border-t pt-2">{itw.notes}</p>}

                  {/* Reponses aux questions */}
                  {itw.answers && (itw.questionsSnapshot || itw.template?.questions) && Object.keys(typeof itw.answers === 'string' ? JSON.parse(itw.answers) : itw.answers).length > 0 && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <h5 className="text-xs font-semibold text-gray-500">Reponses</h5>
                      {(itw.questionsSnapshot || itw.template.questions).map((q, idx) => {
                        if (q.type === 'section') return (
                          <div key={q.id || idx} className="text-xs font-bold text-indigo-600 uppercase pt-1">{q.text || q.question || q.label}</div>
                        );
                        const ans = (typeof itw.answers === 'string' ? JSON.parse(itw.answers) : itw.answers)[q.id || idx];
                        if (!ans) return null;
                        return (
                          <div key={q.id || idx} className="text-xs">
                            <span className="text-gray-500">{q.text || q.question || q.label} :</span>
                            <span className="ml-1 text-gray-700 font-medium">
                              {q.type === 'rating' ? `${ans}/5` : Array.isArray(ans) ? ans.join(', ') : ans}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Boutons action */}
                  <div className="flex gap-2 mt-3 pt-2 border-t">
                    {!itw.completedAt ? (
                      <button onClick={() => startEditInterview(itw)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200">
                        <Edit size={12} /> Conduire
                      </button>
                    ) : (
                      <button onClick={() => startEditInterview(itw)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">
                        <Edit size={12} /> Modifier
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {interviews.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucun entretien</p>}
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CandidateDetail;
